import React, { useEffect, useState } from 'react';
import GlobalTable from '../components/GlobalTable';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import axiosInstance from '../services/axiosInstance';
import qz from 'qz-tray';

const CONNECTION_TYPES = [
    { label: 'LAN', value: 'LAN' },
    { label: 'USB', value: 'USB' },
    { label: 'BLUETOOTH', value: 'BLUETOOTH' },
];

const PRINTER_PROFILES = [
    { label: 'EPSON', value: 'EPSON' },
    { label: 'XPRINTER', value: 'XPRINTER' },
    { label: 'GENERIC', value: 'GENERIC' },
];

const PrintersPage = () => {
    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingPrinter, setEditingPrinter] = useState(null);

    useEffect(() => {
        fetchPrinters();
    }, []);

    const fetchPrinters = async () => {
        try {
            const res = await axiosInstance.get('/api/printers/');
            setPrinters(res.data || []);
        } catch (e) {
            handleApiError(e);
        } finally {
            setLoading(false);
        }
    };

    const openNew = () => {
        setEditingPrinter({
            name: '',
            connection_type: 'LAN',
            ip_address: '',
            port: 9100,
            printer_profile: 'EPSON',
            line_width: 48,
            can_print_receipt: true,
            can_print_label: false,
            can_print_barcode: true,
            is_active: true,
        });
        setDialogVisible(true);
    };

    const onRowEditComplete = async (e) => {
        const { newData } = e;

        try {
            await axiosInstance.patch(
                `/api/printers/${newData.id}/`,
                {
                    name: newData.name,
                    connection_type: newData.connection_type,
                    ip_address: newData.ip_address,
                    can_print_receipt: newData.can_print_receipt,
                    can_print_label: newData.can_print_label,
                    can_print_barcode: newData.can_print_barcode,
                }
            );
            handleApiSuccess('Printer updated');
            fetchPrinters();
        } catch (err) {
            handleApiError(err);
        }
    };


    const savePrinter = async () => {
        try {
            if (editingPrinter.id) {
                await axiosInstance.patch(
                    `/api/printers/${editingPrinter.id}/`,
                    editingPrinter
                );
                handleApiSuccess('Printer updated');
            } else {
                await axiosInstance.post('/api/printers/', editingPrinter);
                handleApiSuccess('Printer created');
            }
            setDialogVisible(false);
            fetchPrinters();
        } catch (e) {
            handleApiError(e);
        }
    };

    /* ---------------- TEST PRINT (FRONTEND ONLY) ---------------- */

    const testPrint = async (printer) => {
        try {
            await qz.websocket.connect();

            const ESC = '\x1B';
            const GS = '\x1D';

            const payload =
                ESC + '@' +
                ESC + 'a' + '\x01' +
                ESC + 'E' + '\x01' +
                'Goldfish BSS\n' +
                ESC + 'E' + '\x00' +
                'Printer Test OK\n\n' +
                new Date().toLocaleString() + '\n\n\n' +
                GS + 'V' + '\x00';

            await qz.print({
                printer: { name: printer.name },
                data: [{
                    type: 'raw',
                    format: 'command',
                    data: payload
                }]
            });

            handleApiSuccess('Test print sent');
        } catch (e) {
            handleApiError(e);
        }
    };

    const actionTemplate = (row) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-print"
                className="p-button-sm p-button-secondary"
                onClick={() => testPrint(row)}
                tooltip="Test Print"
            />
        </div>
    );

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
            sortable: true,
            filtered: true,
        },
        {
            Header: 'Type',
            accessor: 'connection_type',
            sortable: true,
            filtered: true,
        },
        {
            Header: 'IP Address',
            accessor: 'ip_address',
            sortable: true,
            filtered: true,
        },
        {
            Header: 'Receipt',
            accessor: 'can_print_receipt',
            bodyTemplate: (row) => (
                <Checkbox checked={!!row.can_print_receipt} disabled />
            ),
        },
        {
            Header: 'Label',
            accessor: 'can_print_label',
            bodyTemplate: (row) => (
                <Checkbox checked={!!row.can_print_label} disabled />
            ),
        },
        {
            Header: 'Barcode',
            accessor: 'can_print_barcode',
            bodyTemplate: (row) => (
                <Checkbox checked={!!row.can_print_barcode} disabled />
            ),
        },
        {
            Header: 'Test Print',
            accessor: 'actions',
            bodyTemplate: (row) => actionTemplate(row),
        },
    ];


    return (
        <div>
            <div className='containers'>
            <GlobalTable
                columns={columns}
                data={printers}
                loading={loading}
                tabletitle="Printers"
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                headerAction={
                    <Button
                        label="Add Printer"
                        icon="pi pi-plus"
                        onClick={openNew}
                    />
                }
                customEditors = {{
                    connection_type: (options) => (
                        <Dropdown
                            value={options.rowData.connection_type}
                            options={CONNECTION_TYPES}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.connection_type = e.value)
                                )
                            }
                        />
                    ),
                    can_print_receipt: (options) => (
                        <Dropdown
                            value={options.rowData.can_print_receipt}
                            options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false },
                            ]}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.can_print_receipt = e.value)
                                )
                            }
                        />
                    ),
                    can_print_label: (options) => (
                        <Dropdown
                            value={options.rowData.can_print_label}
                            options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false },
                            ]}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.can_print_label = e.value)
                                )
                            }
                        />
                    ),
                    can_print_barcode: (options) => (
                        <Dropdown
                            value={options.rowData.can_print_barcode}
                            options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false },
                            ]}
                            onChange={(e) =>
                                options.editorCallback(
                                    (options.rowData.can_print_barcode = e.value)
                                )
                            }
                        />
                    ),
                }}
            />

            <Dialog
                header={'New Printer'}
                visible={dialogVisible}
                style={{ width: '35rem' }}
                modal
                onHide={() => setDialogVisible(false)}
            >
                {editingPrinter && (
                    <div className="p-fluid">
                        <div className="field">
                            <label>Name</label>
                            <InputText
                                value={editingPrinter.name}
                                onChange={(e) =>
                                    setEditingPrinter({ ...editingPrinter, name: e.target.value })
                                }
                            />
                        </div>

                        <div className="field">
                            <label>Connection Type</label>
                            <Dropdown
                                value={editingPrinter.connection_type}
                                options={CONNECTION_TYPES}
                                onChange={(e) =>
                                    setEditingPrinter({ ...editingPrinter, connection_type: e.value })
                                }
                            />
                        </div>

                        {editingPrinter.connection_type === 'LAN' && (
                            <>
                                <div className="field">
                                    <label>IP Address</label>
                                    <InputText
                                        value={editingPrinter.ip_address || ''}
                                        onChange={(e) =>
                                            setEditingPrinter({ ...editingPrinter, ip_address: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="field">
                                    <label>Port</label>
                                    <InputText
                                        value={editingPrinter.port}
                                        onChange={(e) =>
                                            setEditingPrinter({ ...editingPrinter, port: e.target.value })
                                        }
                                    />
                                </div>
                            </>
                        )}

                        <div className="field">
                            <label>Printer Profile</label>
                            <Dropdown
                                value={editingPrinter.printer_profile}
                                options={PRINTER_PROFILES}
                                onChange={(e) =>
                                    setEditingPrinter({ ...editingPrinter, printer_profile: e.value })
                                }
                            />
                        </div>

                        <div className="field-checkbox">
                            <Checkbox
                                checked={editingPrinter.can_print_receipt}
                                onChange={(e) =>
                                    setEditingPrinter({ ...editingPrinter, can_print_receipt: e.checked })
                                }
                            />
                            <label>Can Print Receipt (ESC/POS)</label>
                        </div>

                        <div className="field-checkbox">
                            <Checkbox
                                checked={editingPrinter.can_print_label}
                                onChange={(e) =>
                                    setEditingPrinter({ ...editingPrinter, can_print_label: e.checked })
                                }
                            />
                            <label>Can Print Label (TSPL)</label>
                        </div>

                        <div className="field-checkbox">
                            <Checkbox
                                checked={editingPrinter.can_print_barcode}
                                onChange={(e) =>
                                    setEditingPrinter({ ...editingPrinter, can_print_barcode: e.checked })
                                }
                            />
                            <label>Can Print Barcode</label>
                        </div>

                        <div className="flex justify-content-end mt-3">
                            <Button
                                label="Save"
                                icon="pi pi-save"
                                onClick={savePrinter}
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
        </div>
    );
};

export default PrintersPage;
